import React from "react";
import { radarShortPosition } from "../agent-radar/radarFormat";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { AvatarSquare, PositionPill, Eyebrow, RadarMarkGlyph, GLYPH } from "./v2";

// Read-only snapshot preview card for the Share-to-Chat sheet (frames 1n desktop
// / 1o mobile, R5). Rendered inside the platform-standard ShareActionSheet via
// its additive `preview` slot (D4: the recipient picker itself stays the
// platform standard; only this preview card is artboard-styled). Shows the S3
// radar mark on a blue tile, the list name, "n players · shared by X · <Club>",
// mini player rows (desktop only — frame 1o intentionally drops them), and the
// read-only privacy footnote. Notes/statuses are deliberately NOT shown (they
// stay private, R5). Real counts drive the rows/overflow — never a cap.
const ClubRadarSharePreview = ({ listName, count = 0, sharedBy, clubName, players = [] }) => {
  const mobile = useMediaQuery("(max-width: 767px)");
  const rows = players.slice(0, 4);
  const overflow = Math.max(0, count - rows.length);
  const sub = [
    `${count} player${count === 1 ? "" : "s"}`,
    sharedBy ? `shared by ${sharedBy}` : null,
    clubName,
  ].filter(Boolean).join(` ${GLYPH.middot} `);
  return (
    <div>
      <Eyebrow fontSize={10.5} spacing=".07em" style={{ margin: "0 0 8px" }}>Preview</Eyebrow>
      <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
        {/* Card header — blue tint, S3 radar mark on brand tile */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: mobile ? "11px 13px" : "12px 14px", background: "#E8F1FF" }}>
          <div
            style={{
              width: mobile ? 30 : 34,
              height: mobile ? 30 : 34,
              borderRadius: mobile ? 9 : 10,
              background: "#2283FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <RadarMarkGlyph size={mobile ? 16 : 18} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: mobile ? 12.5 : 13.5, fontWeight: 800, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{listName}</div>
            <div style={{ fontSize: mobile ? 10.5 : 11, color: "#1B6FE0", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
          </div>
        </div>
        {/* Card body — mini player rows on desktop; frame 1o drops them on mobile */}
        {!mobile && (
          <div style={{ padding: "10px 14px", background: "#fff", display: "flex", flexDirection: "column", gap: 7 }}>
            {rows.map((p, i) => {
              const name = p.name || p.player_name || "Player";
              const pos = radarShortPosition(p.position);
              const club = p.club_name || p.club;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <AvatarSquare name={name} initials={p.initials} imageUrl={p.image_url} size={24} radius={7} fontSize={8.5} fontWeight={800} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
                  {pos && <PositionPill pad="1px 6px" fontSize={9.5}>{pos}</PositionPill>}
                  {club && <span style={{ fontSize: 11.5, color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{club}</span>}
                </div>
              );
            })}
            {overflow > 0 && <div style={{ fontSize: 11, color: "#9CA3AF" }}>+{overflow} more</div>}
          </div>
        )}
        {/* Privacy footnote */}
        <div
          style={{
            padding: mobile ? "9px 13px" : "8px 14px",
            background: "#fff",
            borderTop: mobile ? "none" : "1px solid #F1F5F9",
            fontSize: 10.5,
            color: "#9CA3AF",
            lineHeight: 1.45,
          }}
        >
          {mobile
            ? <>Read-only snapshot {GLYPH.middot} notes and statuses stay private</>
            : <>Read-only snapshot {GLYPH.middot} won{GLYPH.apostrophe}t update as the list changes {GLYPH.middot} notes and statuses stay private</>}
        </div>
      </div>
    </div>
  );
};

export default ClubRadarSharePreview;
